import React, {useState} from "react";
import BasketCartSidebar from "../components/BasketCartSidebar";
import BasketFilterCard from "../components/card/BasketFilterCard";
import BasketRateFilterCard from "../components/card/BasketRateFilterCard";
import BasketResultCard from "../components/card/BasketResultCard";
import {GET_BASKET_TICKET_DATA_LIST_API} from "../constants/api_constants";
import {getFetcherBasket} from "../utils/fetcher_basket";
import {ExclamationCircleIcon} from "@heroicons/react/16/solid";
import neverLandGameDataExpansion from "../utils/NeverLandGameDataExpansion";
import useInactivityTimer from "../utils/useInactivityTimer";


const BasketPage = () => {
  const [infoMessage, setInfoMessage] = useState("검색 조건을 선택 해 주세요.");
  const [searchPerformed, setSearchPerformed] = useState(false);  // 검색 여부
  const [filteredOdds, setFilteredOdds] = useState([]); // 개별 선택 배당 (ex: 50,51,52...)
  const [basketTicketList, setBasketTicketList] = useState([]); // 티켓 리스트
  const [isLoading, setIsLoading] = useState(false);
  const [resetFilters, setResetFilters] = useState(false); // 필터 초기화 트리거

  // 5분동안 이벤트 감지 없으면 프로토 페이지로 이동
  useInactivityTimer(300000);

  const handleRateFilterChange = (rates) => {
    setFilteredOdds(rates)
  }

  const handleResetFilters = () => {
    // 출력시 화면 초기화 여부
    setResetFilters(prev => !prev); // 필터 초기화
    setSearchPerformed(false); // 검색 상태도 초기화
    setFilteredOdds([]); // 필터된 배당도 초기화
    setInfoMessage("검색 조건을 선택 해 주세요."); // 메시지 초기화
  }

  const buildQuery = (filters) => {
    // filter -> 파라미터 형식으로 변경
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v)); // 복수 선택
      } else {
        params.append(key, value); // 단일 선택
      }
    });
    return params.toString();
  };


  const handleFilterSearch = async (filters) => {
    // 조건에 맞는 바구니 티켓 리스트 조회하기

    setIsLoading(true); // 로딩 시작
    const fetchData = async () => {
      const res = await getFetcherBasket().get(
        `${GET_BASKET_TICKET_DATA_LIST_API}?${buildQuery(filters)}`
      );
      return res.data;
    };

    try {
      // 1차 호출
      let firstResponse = await fetchData();
      let {responseIsSuccess, responseResultDescription, gameInfo} = firstResponse;

      // 모든 배열이 비어있는지 확인
      const isFirstAllEmpty = Object.values(gameInfo).every(arr => arr.length === 0);

      // 1차 호출 성공 + 데이터 없음 → 2차 호출
      if (responseIsSuccess && isFirstAllEmpty) {
        const secondResponse = await fetchData();
        const {
          responseIsSuccess: secondSuccess,
          responseResultDescription: secondMessage,
          gameInfo: secondGameInfo
        } = secondResponse;
        const isSecondEmpty = Object.values(secondGameInfo).every(arr => arr.length === 0);

        if (secondSuccess && !isSecondEmpty) {
          // 2차 호출 성공 + 데이터 있음 → 정상 처리
          setFilteredOdds([]);
          neverLandGameDataExpansion.updateList(Object.values(secondGameInfo).flat());
          setBasketTicketList(secondGameInfo);
          setSearchPerformed(true);
          return;
        } else {
          // 2차도 비어있을 때
          setSearchPerformed(false);
          setInfoMessage("조건에 일치하는 조합이 없습니다.");
          return;
        }
      }

      // 1차 호출에서 결과값 있음
      if (responseIsSuccess) {
        setFilteredOdds([]);
        neverLandGameDataExpansion.updateList(Object.values(gameInfo).flat());
        setBasketTicketList(gameInfo);
        setSearchPerformed(true);
      } else {
        setSearchPerformed(false);
        setInfoMessage("조건에 일치하는 조합이 없습니다.");
        console.log("경기정보 조회 실패 : ", responseResultDescription);
      }
    } catch (err) {
      console.error("API 호출 오류:", err);
      alert("서버 연결에 실패했습니다.");
    } finally {
      setIsLoading(false); // 로딩 종료
    }
  };


  return (
    <div className="w-full h-[calc(100%-2rem)] flex items-center justify-center">
      <div className="w-[63rem] h-full flex flex-row ">

        <div className="w-[41rem] h-full flex flex-col p-3">
          {/* <-- 조건 선택 영역 --> */}
          <div className="w-full flex flex-col mx-1 font-bold pb-2 whitespace-nowrap gap-y-4">
            {/*조건 선택 카드*/}
            <BasketFilterCard
              onSearch={handleFilterSearch}
              searchPerformed={searchPerformed}
              resetTrigger={resetFilters}
            />

            {/*개별 배당 선택 카드*/}
            {searchPerformed &&
              <BasketRateFilterCard basketTicketList={basketTicketList} onFilterChange={handleRateFilterChange}/>
            }
          </div>

          {/* <-- 결과 카드 리스트 --> */}
          <div className="w-full overflow-y-auto pl-1 py-3">
            {isLoading ? (
              // 로딩 중
              <div className="w-full flex flex-col text-gray-400 justify-center pt-10 items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 mb-4"></div>
                <p className="text-xl">검색 중...</p>
              </div>
            ) : searchPerformed && basketTicketList ? (
              // 결과 있음
              <BasketResultCard filteredOdds={filteredOdds}/>
            ) : (
              // 초기 상태 또는 결과 없음
              <div className="w-full flex flex-col text-gray-400 justify-end pt-10 items-center rounded-lg">
                <ExclamationCircleIcon className="h-14 w-14 mr-3 my-5"/>
                <p className="text-2xl text-center">
                  {infoMessage}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* <------------ 카트사이드바 영역 ------------> */}
        <BasketCartSidebar onResetFilters={handleResetFilters}/>
      </div>
    </div>
  );
}
export default BasketPage;
